import React from "react";
import { PageBuilderFycFragment } from "types/graphql-types";
import FindYourCruise from "components/FindYourCruise";
import { graphql } from "gatsby";
import { stringPresent } from "src/utils/strings";
import { configToFYCQuery } from "./utils";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useLanguage } from "src/utils/localization/languages/I18nContext";

const PageBuilderFyc: React.FC<
  React.PropsWithChildren<PageBuilderFycFragment>
> = ({
  title,
  showDestinationFilter = false,
  showShipFilter = false,
  showExpeditionFilter = false,
  showPromosFilter = false,
  showFeaturesFilter = false,
  showCruiseEventsFilter = false,
  showLastSeenProducts,
  promosContent = [],
  shipsContent = [],
  themedCruisesContent = [],
  cruisesTagCategoryContent = [],
  firstLastDepartureCruiseDates,
  ...props
}) => {
  const geolocation = useGeolocation();
  const language = useLanguage();

  const query = configToFYCQuery(
    props,
    geolocation.countryCode,
    language,
    false
  );
  const hasTitle = stringPresent(title?.localized);

  return (
    <div id="fyc">
      <FindYourCruise
        title={title?.localized}
        showTitle={hasTitle}
        titleAlign="center"
        backgroundColor="neutral10"
        initialSearchQuery={query}
        resultsPageSize={9}
        showExpeditionFilter={showExpeditionFilter}
        specialTypeFilter={undefined}
        showDestinationFilter={showDestinationFilter}
        showShipFilter={showShipFilter}
        showPromosFilter={showPromosFilter}
        showFeaturesFilter={showFeaturesFilter}
        showCruiseEventsFilter={showCruiseEventsFilter}
        showDurationFilter={true}
        firstLastDepartureCruiseDates={firstLastDepartureCruiseDates}
        shipsContent={shipsContent}
        promosContent={promosContent}
        themedCruisesContent={themedCruisesContent}
        cruisesTagCategoryContent={cruisesTagCategoryContent}
        showLastSeenProducts={showLastSeenProducts}
      />
    </div>
  );
};

export default PageBuilderFyc;

export const fragments = graphql`
  fragment PageBuilderFyc on SanityFyc {
    _key
    title {
      localized
    }
    showDestinationFilter
    showShipFilter
    showExpeditionFilter
    showPromosFilter
    showFeaturesFilter
    showCruiseEventsFilter
    showLastSeenProducts

    filterByShip {
      name
    }

    filterByDestination {
      name {
        localized
      }
    }

    filterByOffer {
      ... on SanityExclusiveOfferPerPromo {
        _id
        configuration {
          code
          algoliaCode
        }
      }
    }

    filterByCruiseType
    filterByComboType

    filterByCruise {
      ... on SanityCruise {
        data {
          cruiseCode
        }
      }

      ... on SanitySpecialVoyage {
        data {
          cruiseCode
        }
      }
    }

    filterByCruiseCodes

    filterByCountries {
      ports {
        data {
          code
        }
      }
    }

    filterByPorts {
      data {
        code
      }
    }

    filterByFeatures
    filterByCruiseTagCategories {
      ...FYCCruisesTagCategoryContent
    }
    filterByCruiseGroups
    filterByFareCodes
    firstLastDepartureCruiseDates {
      minYearMonth
      maxYearMonth
    }
    filterByCruisesLaunch {
      cruiseCodes
    }

    filterByLoadStress
    filterByDepartureYears
    filterByDepartureYearsAndMonths {
      year
      months
    }

    filterByLandProgrammes {
      code
    }

    promosContent {
      ...FYCPromosContent
    }
    shipsContent {
      ...FYCShipsContent
    }
    themedCruisesContent {
      ...FYCThemedCruisesContent
    }
    cruisesTagCategoryContent {
      ...FYCCruisesTagCategoryContent
    }
  }
`;
